import { UntypedFormControl } from '@angular/forms';
import { InjectionToken, Type } from '@angular/core';
import { Provider } from '@angular/core';

import { TextWriterBuiderComponent } from './text-writer/text-writer-buider/text-writer-buider.component';
import { TextWriterComponent } from './text-writer/text-writer.component';
import { TranslationSwitcherBuilderComponent } from './translationSwitcher/translationSwitcherBuilder/translationSwitcherBuilder.component';
import { TranslationSwitcherComponent } from './translationSwitcher/translationSwitcher.component';
import { SpeakerBuilderComponent } from './speaker/speaker-builder/speaker-builder.component';
import { SpeakerComponent } from './speaker/speaker.component';
import { SelectorBuilderComponent } from '../../dynamic-components/selector/selector-builder/selector-builder.component';
import { SelectorComponent } from '../../dynamic-components/selector/selector.component';

export type LessonComponentsType = 'selector' | 'speaker' | 'text-writer' | 'verb-summary' | 'word-summary';

export enum LessonComponentEnum {
  Selector = 'selector',
  Speaker = 'speaker',
  TextWriter = 'textWriter',
  VerbSummary = 'verbSummary',
  WordSummary = 'wordSummary',
  TranslationSwitcher = 'translationSwitcher',
  PlayWord = 'playWord',
}

export const LessonComponentBuilders = {
  [LessonComponentEnum.Selector]: SelectorBuilderComponent,
  [LessonComponentEnum.Speaker]: SpeakerBuilderComponent,
  [LessonComponentEnum.TextWriter]: TextWriterBuiderComponent,
  // [LessonComponentEnum.VerbSummary]: VerbSummaryBuilderComponent,
  // [LessonComponentEnum.WordSummary]: WordSummaryBuilderComponent,
  [LessonComponentEnum.TranslationSwitcher]: TranslationSwitcherBuilderComponent,
  // [LessonComponentEnum.PlayWord]: PlayWordBuilderComponent,
};

export const LessonComponents = {
  [LessonComponentEnum.Selector]: SelectorComponent,
  [LessonComponentEnum.Speaker]: SpeakerComponent,
  [LessonComponentEnum.TextWriter]: TextWriterComponent,
  // [LessonComponentEnum.VerbSummary]: VerbSummaryComponent,
  // [LessonComponentEnum.WordSummary]: WordSummaryComponent,
  [LessonComponentEnum.TranslationSwitcher]: TranslationSwitcherComponent,
  // [LessonComponentEnum.PlayWord]: PlayWordComponent,
};

export function getLessonComponentClass(type: LessonComponentsType) {
  return LessonComponents[type];
}

export interface LessonCompSettings {
  response?: string;
  responses?: string; // en caso de que haya multiples respuestas
  options?: string[]; // optiones para visualizar
  text?: string;
  hint?: string;
  explanation?: string;
}

// When the text should be extracted from the component and pass to the lesson/content to be saved/read process by AI.
export interface TextContent {
  text: string;
}

// Make LessonComponentConfiguration generic for settings
export interface LessonComponentConfiguration<T extends LessonCompSettings = LessonCompSettings> {
  id?: string;
  component: LessonComponentEnum | string;
  settings?: T; // for component that need and standards
  inputs?: Record<string, any>; // for whatever component.
}

export interface StorageFile {
  bucket: string;
  url: string;
  path: string; // path where the file is in the storage
}

export interface AudioStorage extends StorageFile {
  text?: string;
  voiceType?: string;
}
// Make SpeakerCompConfiguration extend the generic base interface
export interface SpeakerCompConfiguration extends LessonComponentConfiguration<LessonCompSettings & { voice: string }> {
  audio: AudioStorage;
  // settings is now inherited correctly via the generic type argument
}

export interface LessonComponentInterface {
  // Update config to use the generic form
  config: LessonComponentConfiguration<any>;

  control?: UntypedFormControl;

  validate?(): boolean | null;
  evaluate?(): boolean | null;
}

export const LESSONS_TOKEN = new InjectionToken<LessonsAbstractService>('Lessons Service');

export interface ImgStorageData extends StorageFile {
  fullPath: string; // path + name
  resolutions: any;
  resolution: string;
}

export type LessonImage = ImgStorageData & { type: string };

export interface DynamicContentComponent {
  id: string; // duplicated id
  component: LessonComponentEnum; // name of the component
  inputs: Record<string, any>;
  config: LessonComponentConfiguration<any>;
}

type StorageType = StorageFile & { type: string };

export interface Media {
  audios?: StorageType[];
  images?: StorageType[];
}

export interface ILessonExtra {
  notionPageId?: string;
}

// Lesson will renamed to content
export interface ContentMetadata {
  title: string;
  description: string;
  isPublished: boolean;
  tags: string[];
  banner: StorageType;
  prompt: string;
  generatedByAI: boolean;
}

export interface ILesson {
  // me faltan los comentarios
  _id: any;
  id: string;
  metadata?: ContentMetadata;
  title: string;
  description: string;
  isPublished: boolean;
  // isPrivate: boolean;
  textCoded: string;
  authorId: string;
  authorEmail: string;
  tags: string[];
  media: Media;
  banner?: StorageType;
  // Update components array to use generic form
  components: Array<LessonComponentConfiguration<any>>;
  dynamicComponents: Record<string, DynamicContentComponent>;
  baseLang: string;
  targetLang: string;
  appExtension: Record<string, any>;

  generatedByAI: boolean;
  prompt: string;
  level: number;
  extras: ILessonExtra;
}

export interface ILessonWithTaken extends ILesson {
  // FIXME: moved to lessons lib

  taken: any;
  createdAt: Date;
  updatedAt: Date;
}

// Think on someway of getting the toast alerts service

export interface ILessonTaken {
  id: string;
  score: number;
  goalCompleted: boolean;
  status: string;
  lastAccess: Date;
}

export interface LessonPrompts {
  description: (lesson: ILesson) => string;
  banner: (lesson: ILesson) => string;
  content: (lesson: ILesson) => string;
}

export abstract class LessonsAbstractService {
  abstract getLessons(paginator?: any): Promise<any>;
  abstract getLesson(id: string): Promise<any>;
  abstract postLesson(lesson: ILesson): Promise<any>;
  abstract updateLesson(lesson: ILesson): Promise<any>;
  abstract deleteLesson(id: string): Promise<any>;
  abstract generateLesson(lesson: ILesson): Promise<any>;
  abstract postGenerateByAI(id: string): Promise<any>;
  abstract extractTextFromHtml(html: string): string;
  abstract postImproveMDWithAI(lessonId: string, markdownText: string): Promise<any>; // Added for AI Markdown improvement
  abstract saveTakenLesson(lesson: ILessonTaken): Promise<any>;
  abstract getPrompts(): LessonPrompts;
}

// my-service.provider.ts
export function provideLessonsService(serviceImplementation: Type<LessonsAbstractService>): Provider[] {
  return [
    {
      provide: LESSONS_TOKEN,
      useExisting: serviceImplementation,
    },
  ];
}
